import { getAuthToken } from '@/lib/auth-token';
import { APP_TARGET } from '@/lib/target';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

// Embedded boards expose a flat /api/* surface (no workspace/project
// context — the board IS the singleton project). The SPA's data layer
// keeps building cloud-shaped paths so its components stay
// target-agnostic; rewriteEmbeddedPath strips the
// /api/workspaces/embedded/projects/local/ prefix at the seam.
const EMBEDDED_PATH_PREFIX = '/api/workspaces/embedded/projects/local';

export function rewriteEmbeddedPath(path: string): string {
  if (APP_TARGET !== 'embedded') return path;
  if (path.startsWith(EMBEDDED_PATH_PREFIX)) {
    return `/api${path.slice(EMBEDDED_PATH_PREFIX.length)}`;
  }
  return path;
}

export class ApiError extends Error {
  /**
   * @param body — full parsed JSON response body. Useful when the server
   * attaches structured context to a non-2xx (e.g. /deployments returns
   * 412 with `{ error, reconciliation_report }` so the editor can render
   * the diff inline rather than showing a generic "blocked" banner).
   */
  constructor(
    public status: number,
    public code: string,
    message: string,
    public body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = await getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    // FormData bodies need the browser/undici to inject a multipart
    // Content-Type with a generated boundary; setting it manually
    // strips the boundary and breaks the server-side parse.
    headers.set('Content-Type', 'application/json');
  }
  const rewritten = rewriteEmbeddedPath(path);
  const url = rewritten.startsWith('http') ? rewritten : `${API_BASE}${rewritten}`;
  const res = await fetch(url, { ...init, headers });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const errBody = body as { error?: { code?: string; message?: string } } | null;
    const code = errBody?.error?.code ?? 'unknown';
    const message = errBody?.error?.message ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, code, message, body);
  }
  return body as T;
}
